import { redirect } from "@tanstack/react-router";

import { User } from "@/stores/auth/auth-store";

import { Permission, USER_ROLES, UserRole } from "./permissions-registry";

/**
 * Configuration for route-level authorization
 */
export interface RouteAuthConfig {
  /**
   * Required permission(s) to access this route
   * If multiple permissions are provided, uses OR logic (user needs ANY of them)
   */
  requiredPermissions?: Permission | Permission[];

  /**
   * Required role(s) to access this route
   * If multiple roles are provided, uses OR logic (user needs ANY of them)
   */
  requiredRoles?: UserRole | UserRole[];

  /**
   * If true, user must have ALL specified permissions (AND logic)
   * @default false
   */
  requireAllPermissions?: boolean;

  /**
   * If true, user must have ALL specified roles (AND logic)
   * @default false
   */
  requireAllRoles?: boolean;

  /**
   * Where to redirect if user doesn't have access
   * @default '/' (dashboard)
   */
  redirectTo?: string;

  /**
   * Custom error message for unauthorized access
   */
  unauthorizedMessage?: string;
}

/**
 * Create a beforeLoad guard for TanStack Router that checks permissions/roles
 *
 * @example
 * ```ts
 * export const Route = createFileRoute('/admin/users')({
 *   beforeLoad: createRouteGuard({
 *     requiredPermissions: 'users:view',
 *     requiredRoles: ['ADMIN', 'KEYUSER']
 *   }),
 *   component: UsersPage
 * });
 * ```
 */
export const createRouteGuard = (authConfig: RouteAuthConfig) => {
  return ({ context, location }: { context: { auth: { isAuthenticated: boolean; user: User | null } }; location: { href: string } }) => {
    const { auth } = context;

    // First check if user is authenticated
    if (!auth.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }

    const user = auth.user;
    if (!user) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }

    // If no authorization requirements are specified, allow access
    if (!authConfig.requiredPermissions && !authConfig.requiredRoles) {
      return;
    }

    // Check permissions and roles using the same logic as the hook
    const userPermissions = user.permissions || [];
    const userRoles = user.roles || [];

    // Normalize permissions and roles to arrays
    const requiredPermissions = authConfig.requiredPermissions
      ? Array.isArray(authConfig.requiredPermissions)
        ? authConfig.requiredPermissions
        : [authConfig.requiredPermissions]
      : [];

    const requiredRoles = authConfig.requiredRoles ? (Array.isArray(authConfig.requiredRoles) ? authConfig.requiredRoles : [authConfig.requiredRoles]) : [];

    // Check permissions
    let hasRequiredPermissions = true;
    if (requiredPermissions.length > 0) {
      hasRequiredPermissions = authConfig.requireAllPermissions
        ? requiredPermissions.every((permission) => userPermissions.includes(permission))
        : requiredPermissions.some((permission) => userPermissions.includes(permission));
    }

    // Check roles
    let hasRequiredRoles = true;
    if (requiredRoles.length > 0) {
      hasRequiredRoles = authConfig.requireAllRoles ? requiredRoles.every((role) => userRoles.includes(role)) : requiredRoles.some((role) => userRoles.includes(role));
    }

    // If user doesn't have required access, redirect
    if (!hasRequiredPermissions || !hasRequiredRoles) {
      throw redirect({
        to: authConfig.redirectTo || "/",
        search: {
          error: authConfig.unauthorizedMessage || "Insufficient permissions to access this page",
        } as Record<string, string>,
      });
    }
  };
};

/**
 * Pre-configured route guards for common scenarios
 */
export const RouteGuards = {
  /**
   * Admin only routes (ROLE_SUPER_ADMIN)
   */
  adminOnly: () =>
    createRouteGuard({
      requiredRoles: USER_ROLES.ROLE_SUPER_ADMIN,
      unauthorizedMessage: "Admin access required",
    }),

  /**
   * Company owner routes (ROLE_COMPANY_OWNER)
   */
  companyOwner: () =>
    createRouteGuard({
      requiredRoles: USER_ROLES.ROLE_COMPANY_OWNER,
      unauthorizedMessage: "Company owner access required",
    }),

  /**
   * Admin or Company Owner routes
   */
  adminOrCompanyOwner: () =>
    createRouteGuard({
      requiredRoles: [USER_ROLES.ROLE_SUPER_ADMIN, USER_ROLES.ROLE_COMPANY_OWNER],
      unauthorizedMessage: "Admin or Company Owner access required",
    }),

  /**
   * User management routes
   */
  userManagement: () =>
    createRouteGuard({
      requiredPermissions: "users:view",
      unauthorizedMessage: "User management permission required",
    }),

  /**
   * Settings access
   */
  settings: () =>
    createRouteGuard({
      requiredPermissions: "settings_general:view",
      unauthorizedMessage: "Settings access required",
    }),

  /**
   * Master data access
   */
  masterData: () =>
    createRouteGuard({
      requiredPermissions: "users:view",
      unauthorizedMessage: "Master data access required",
    }),
};

/**
 * Helper to combine multiple auth requirements (useful for complex routes)
 */
export const combineAuthRequirements = (...configs: RouteAuthConfig[]): RouteAuthConfig => {
  const combined: RouteAuthConfig = {};

  const allPermissions: Permission[] = [];
  const allRoles: UserRole[] = [];

  for (const config of configs) {
    if (config.requiredPermissions) {
      const perms = Array.isArray(config.requiredPermissions) ? config.requiredPermissions : [config.requiredPermissions];
      allPermissions.push(...perms);
    }

    if (config.requiredRoles) {
      const roles = Array.isArray(config.requiredRoles) ? config.requiredRoles : [config.requiredRoles];
      allRoles.push(...roles);
    }
  }

  if (allPermissions.length > 0) {
    combined.requiredPermissions = allPermissions;
  }

  if (allRoles.length > 0) {
    combined.requiredRoles = allRoles;
  }

  return combined;
};
